import React, { useState, useContext } from 'react';
import { Layout } from './Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Alert, AlertDescription } from './ui/alert';
import { Separator } from './ui/separator';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from './ui/breadcrumb';
import { useAuth } from './AuthContext';
import {
  User,
  Mail,
  Calendar,
  Shield,
  Save,
  Loader2,
  CheckCircle,
  AlertCircle,
  Home,
  Settings as SettingsIcon,
  Lock,
  Bell,
  Activity
} from 'lucide-react';
import { format, set } from 'date-fns';
import { toast } from 'sonner';
import { AppContext } from '../src/App';
import { apiClient } from '@/Utils/apiClient';
import { VIEW_TO_PATH } from '@/routes/config';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";

export const Profile: React.FC = () => {
  const { user, token, updateProfile, signOut } = useAuth();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { setCurrentView } = useContext(AppContext);

  const [isResetting, setIsResetting] = useState(false);
  const [signoutLoading, setSignoutLoading] = useState(false);

  const [showResetDialog, setShowResetDialog] = useState(false);

  if (!user) {
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsSubmitting(true);

    try {
      const result = await updateProfile({
        name: formData.name,
        lastName: formData.lastName,
        email: formData.email,
      });

      if (result.success) {
        setSuccess('Profile updated successfully');
        setIsEditing(false);
        toast.success('Profile updated successfully');
      } else {
        setError(result.error || 'Failed to update profile');
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: user.name,
      lastName: user.lastName,
      email: user.email,
    });
    setIsEditing(false);
    setError('');
    setSuccess('');
  };

  const handleLogoutAction = () => {
    signOut();
    setCurrentView("");
    const targetPath = VIEW_TO_PATH['signIn'];
    if (targetPath) navigate(targetPath);
    else navigate('/signIn');
  };

  const handleLogout = async () => {
    setSignoutLoading(true);
    try {
      const response = await apiClient(`auth/signout`, {
        method: 'POST',
        token

        // headers: {
        //   'Content-Type': 'application/json',
        //   'Authorization': `Bearer ${token}`
        // }
      });
      const data = await response.json();
      if (!data.success) throw new Error(data.error || 'Request failed');
      toast.success(data?.message || 'Signed out successfully');
      handleLogoutAction();
    } catch (err: any) {

      if (err.response?.status === 401 || err.status === 401) {
        handleLogoutAction();
        toast.success("Signed out successfully");
      } else {
        toast.error(err.message || "Failed to sign out");
      }
    } finally {
      setSignoutLoading(false);
    }

  }
  const handlePasswordResetRequest = async () => {
    setIsResetting(true);
    try {
      const email = formData?.email; // Declare a variable named 'email'

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}auth/password/reset`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email }),
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Request failed");
      }

      toast.success('Password reset email sent!', {
        description: 'Please check your inbox to continue.',
      });
    } catch (err: any) {
      console.log(err);
      toast.error(err.message || 'Failed to send reset email');
    } finally {
      setIsResetting(false);
      setShowResetDialog(false);
    }
  };

  const getRoleColor = (role: string) => {
    const colors = {
      'Admin': 'bg-red-100 text-red-800',
      'Supervisor': 'bg-blue-100 text-blue-800',
      'Auditor': 'bg-green-100 text-green-800',
      'Viewer': 'bg-gray-100 text-gray-800'
    };
    return colors[role as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getInitials = (name: string, lastName: string) => {
    return `${name?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  };

  return (
    <Layout>
      <div className="p-8">
        {/* Breadcrumb Navigation */}
        <div className="mb-6">
          <Breadcrumb>
            <BreadcrumbList>
              {/* <BreadcrumbItem>
                <BreadcrumbLink href="#" className="flex items-center gap-2">
                  <Home className="w-4 h-4" />
                  Dashboard
                </BreadcrumbLink>
              </BreadcrumbItem> 
              <BreadcrumbSeparator /> */}
              <BreadcrumbItem>
                <BreadcrumbPage className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Profile
                </BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-gray-900 mb-2">User Profile</h1>
          <p className="text-gray-600">
            Manage your account information and preferences
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Profile Overview */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader className="text-center">
                <div className="flex justify-center mb-4">
                  <Avatar className="w-20 h-20">
                    <AvatarFallback className="bg-blue-100 text-blue-700 text-lg">
                      {getInitials(user?.name, user?.lastName)}
                    </AvatarFallback>
                  </Avatar>
                </div>
                <CardTitle>{[user?.name, user?.lastName].filter(Boolean).join(' ') || "None"}</CardTitle>
                <CardDescription>{user?.email}</CardDescription>
                <div className="flex justify-center mt-2">
                  <Badge className={`${getRoleColor(user?.role)} border-0`}>
                    {user?.roles?.length ? user?.roles[0] : ""}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3 text-sm text-gray-600">
                  {/* <Calendar className="w-4 h-4" /> */}
                  {/* <span>Joined {format(new Date(user?.createdAt), 'MMM dd, yyyy')}</span> */}
                </div>
                {user?.lastLogin && (
                  <div className="flex items-center gap-3 text-sm text-gray-600">
                    <Activity className="w-4 h-4" />
                    {/* <span>Last login {format(new Date(user?.lastLogin), 'MMM dd, yyyy HH:mm')}</span> */}
                  </div>
                )}
                {/* <Separator />
                <Button
                  variant="outline"
                  onClick={() => { handleLogout() }}
                  className="w-full"
                  disabled={signoutLoading}
                >
                  Sign Out
                </Button> */}
              </CardContent>
            </Card>
          </div>

          {/* Profile Settings */}
          <div className="lg:col-span-2 space-y-6">
            {/* Personal Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Personal Information
                </CardTitle>
                <CardDescription>
                  Update your personal details and contact information
                </CardDescription>
              </CardHeader>
              <CardContent>
                {error && (
                  <Alert variant="destructive" className="mb-4">
                    <AlertCircle className="w-4 h-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                {success && (
                  <Alert className="mb-4 border-green-200 bg-green-50">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <AlertDescription className="text-green-800">{success}</AlertDescription>
                  </Alert>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="full-name">First Name</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          id="full-name"
                          value={`${formData.name}`.trim()}
                          className="pl-10"
                          disabled
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="full-name">Last Name</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          id="full-name"
                          value={`${formData.lastName}`.trim()}
                          className="pl-10"
                          disabled
                          placeholder='None'
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="profile-email">Email Address</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          id="profile-email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                          className="pl-10"
                          disabled={!isEditing || isSubmitting}
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Role</Label>
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-gray-400" />
                      <Badge className={`${getRoleColor(user?.role)} border-0`}>
                        {user?.roles}
                      </Badge>
                      <span className="text-sm text-gray-500">
                        (Contact administrator to change role)
                      </span>
                    </div>
                  </div>

                  {/* <div className="flex gap-3 pt-4">
                    {!isEditing ? (
                      <Button
                        type="button"
                        onClick={() => setIsEditing(true)}
                      >
                        <SettingsIcon className="w-4 h-4 mr-2" />
                        Edit Profile
                      </Button>
                    ) : (
                      <>
                        <Button
                          type="submit"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <Save className="w-4 h-4 mr-2" />
                              Save Changes
                            </>
                          )}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleCancel}
                          disabled={isSubmitting}
                        >
                          Cancel
                        </Button>
                      </>
                    )}
                  </div> */}
                </form>
              </CardContent>
            </Card>
            {  /* CHANGED: New Security Card for Password Reset */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lock className="w-5 h-5" />
                  Security Settings
                </CardTitle>
                <CardDescription>
                  Manage your account security settings
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
                <div>
                  <h3 className="font-medium ">Reset Password</h3>
                  <p className="text-sm text-gray-600">
                    Click the button to receive a password reset link in your email.
                  </p>
                </div>
                <Button
                  variant="outline"
                  className="mt-4 sm:mt-0 transition transform duration-150 active:scale-95 "
                  onClick={(e) => {
                    e.preventDefault();
                    setShowResetDialog(true);
                  }}
                  disabled={isResetting}
                >
                  {/* {isResetting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} */}
                  Reset Password
                </Button>
              </CardContent>
            </Card>
            <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Reset Password</DialogTitle>
                  <DialogDescription>
                    Are you sure you want to send a password reset link?
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button variant="outline" className="" onClick={() => setShowResetDialog(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handlePasswordResetRequest} disabled={isResetting}>
                    {isResetting ? 'Sending...' : 'Confirm'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            {/* Security Settings */}
            {/* <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lock className="w-5 h-5" />
                  Security Settings
                </CardTitle>
                <CardDescription>
                  Manage your account security and authentication preferences
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <p className="font-medium">Password</p>
                    <p className="text-sm text-gray-600">Last updated {format(new Date(user.updatedAt), 'MMM dd, yyyy')}</p>
                  </div>
                  <Button variant="outline" disabled>
                    Change Password
                  </Button>
                </div>

                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <p className="font-medium">Two-Factor Authentication</p>
                    <p className="text-sm text-gray-600">Add an extra layer of security to your account</p>
                  </div>
                  <Badge variant="secondary">Not Configured</Badge>
                </div>
              </CardContent>
            </Card> */}

            {/* Preferences */}
            {/* <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="w-5 h-5" />
                  Notification Preferences
                </CardTitle>
                <CardDescription>
                  Configure how you want to receive notifications
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <p className="font-medium">Email Notifications</p>
                    <p className="text-sm text-gray-600">Receive audit alerts and system updates via email</p>
                  </div>
                  <Badge className="bg-green-100 text-green-800 border-0">Enabled</Badge>
                </div>

                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <p className="font-medium">Audit Reminders</p>
                    <p className="text-sm text-gray-600">Get reminded about pending audit reviews</p>
                  </div>
                  <Badge className="bg-green-100 text-green-800 border-0">Enabled</Badge>
                </div>
              </CardContent>
            </Card> */}
          </div>
        </div>
      </div>
    </Layout>
  );
};